// vendor
import { SetupContext, ref } from "@vue/composition-api";
// project
import { useFavourites } from "@/hooks/useFavourites";
import { useTags } from "@/hooks/useTags";
import * as AppModal from "@/AppModal";
import { useTranslate } from "@lang";

type Config = {
	context:SetupContext;
};

export const useTagManager = (c:Config) => {

	const dropTarget = ref<string|null>();

	const { translate } = useTranslate();

	const {
		sortedTags,
		editTag,
		tagItem,
		deleteTag,
		createTag,
	} = useTags();

	const {
		isFavourite,
		addFavourite,
		removeFavourite
	} = useFavourites();

	const onCreate = async() => {
		AppModal.editText({
			title:translate("tags.label.newTag"),
			value:translate("tags.label.newTag"),
			validator(v){
				return v.length > 2;
			},
		},  (v:string) => {
			createTag(v);
		});
	};

	const openTagContext = (e:any, id:string) => {
		const tag = sortedTags.value.find(t => t._id === id);
		if(!tag){ return; }


		const options:any[] = [];

		if(!isFavourite(id)){
			options.push({
				name:translate("action.addToFavourites"),
				icon:"mdi.star",
				fn:() => addFavourite(id, "tag"),
				order:0,
			});
		}
		else {
			options.push({
				name:translate("action.removeFromFavourites"),
				icon:"mdi.star-outline",
				fn:() => removeFavourite(id),
				order:0,
			});
		}

		options.push({
			name:translate("action.rename"),
			icon:"mdi.pencil",
			fn(){
				AppModal.editText({
					title:translate("tags.action.renameTag"),
					value:tag.name,
					validator(v){
						return v !== tag.name && v.length > 2;
					}
				}, (v:string) => {
					editTag(tag._id, { name:v });
				});
			},
			order:1,
		});

		options.push({
			name:translate("action.setColor"),
			icon:"mdi.format-color-fill",
			fn(){
				AppModal.editColor({
					title:translate("tags.action.setTagColor"),
					value:tag.color,
					validator(v){
						return tag.color !== v; 
					},
				}, (v:string) => {
					editTag(tag._id, { color:v });
				});
			},
			order:1,
		});

		options.push({
			name:translate("action.delete"),
			icon:"mdi.trash-can",
			fn(){

				AppModal.confirm({
					title:translate("tags.action.deleteTag"),
					variant:"danger",
					description:translate("prompts.message.irreversible"),
					okText:translate("prompts.action.understood"),
				}, () => {
					deleteTag(tag._id);
				});
			},
			order:2,
		});

		AppModal.context(e, options);
	};

	const handleTagDrop = (e:any, tag:string) => {
		const dataString = e.dataTransfer.getData("tagTarget");
		if(dataString){
			const data:{
				type:"asset"|"study";
				item:string;
			} = JSON.parse(dataString);

			if(data.type && data.item){
				tagItem(tag, data.item, data.type);
			}
		}
		dropTarget.value = null;
	};
	

	return {
		sortedTags,
		dropTarget,
		openTagContext,
		handleTagDrop,
		createTag:onCreate,
	};

};